/**
 * GuardIA DriveScore por frecuencia — modelo v2.
 *
 * La conversión de cada componente está fijada por ADR-016: E = 100 × N / D y
 * S = clamp(100 − E, 0, 100), idéntica para las cuatro familias. N son los
 * episodios finales consolidados del Pipeline V5 y D la distancia elegible del
 * mismo análisis. El manual `v3-acc25` sigue siendo la fuente de los pesos y de
 * la clasificación global; sus bandas antiguas ya no convierten tasas a puntos.
 *
 * ## Cómo funciona, y por qué está mejor pensado que lo que yo había hecho
 *
 * El score de un componente sí puede llegar a cero: 100 o más episodios por
 * cada 100 km saturan el descuento. No hay suelo artificial ni escala especial
 * para giros.
 *
 * ## La regla que sí se respeta al pie de la letra
 *
 * Un componente que **no se pudo medir** no se puntúa como 100. Se saca del
 * promedio y los pesos se renormalizan sobre los que sí se midieron. Si a la
 * cadencia del equipo el detector de frenadas no alcanza a dispararse, su cero
 * significa «no medimos» y darle 100 sería premiar la falta de datos — que es
 * el error que este sistema entero existe para no cometer (ADR-007).
 */

export const DRIVESCORE_VERSION = "guardia-drivescore-frequency-v2" as const;
/** Manual del que se conservan los pesos y la clasificación global. */
export const DRIVESCORE_MANUAL = "v3-acc25" as const;

export type ScoreComponent = "velocidadAlta" | "frenadas" | "aceleraciones" | "giros";

/** Ponderaciones oficiales del manual, sección 2. Suman 100. */
export const COMPONENT_WEIGHTS: Record<ScoreComponent, number> = {
  velocidadAlta: 35,
  frenadas: 25,
  aceleraciones: 20,
  giros: 20,
};

export const COMPONENT_LABELS: Record<ScoreComponent, string> = {
  velocidadAlta: "Velocidad alta operativa",
  frenadas: "Frenadas bruscas",
  aceleraciones: "Aceleraciones bruscas",
  giros: "Giros de riesgo",
};

/** Lectura general del DriveScore (manual, sección 6). */
export type DriveScoreReading = {
  from: number;
  risk: string;
  clientState: string;
  /**
   * `null` cuando el puntaje se calculó sobre menos de los cuatro
   * componentes, o cuando la probabilidad está retirada. Ver `readingFor`.
   */
  accidentProbability: string | null;
  /**
   * Solo con la política `RETIRADA`: la probabilidad no se publica porque sus
   * porcentajes no tienen fuente verificada.
   */
  accidentProbabilityWithdrawn?: true;
  /**
   * Solo con la política `EN_ACTUALIZACION`: el apartado se conserva y dice
   * «En actualización», sin porcentaje.
   */
  accidentProbabilityInUpdate?: true;
};

/**
 * Qué se hace con la probabilidad de accidente de la sección 6 del manual.
 *
 * - `PUBLICAR_BANDAS_DEL_MANUAL`: lo de hoy. Con los cuatro componentes medidos
 *   se publica la banda («6,1 % a 9 % o mejor» … «16,1 % a 35 %»).
 * - `RETIRADA`: el camino (b) del ADR-008. Se publican el puntaje y la lectura
 *   de riesgo, y la probabilidad no, en ningún caso.
 *
 * Por qué existe la opción: el ADR-008 dejó la procedencia de esos porcentajes
 * en `UNVERIFIED` y bloqueando el primer informe a un cliente. Traer el manual
 * al repo no los vuelve aplicables: haría falta saber de qué datos salen, de qué
 * población, en qué período y con qué método, y si valen para esta flota, su
 * cadencia y estos umbrales.
 */
export type AccidentProbabilityPolicy = "PUBLICAR_BANDAS_DEL_MANUAL" | "RETIRADA" | "EN_ACTUALIZACION";

/**
 * Política vigente: `EN_ACTUALIZACION` (decisión de Fabián García, 08/10/2026,
 * ADR-014).
 *
 * El apartado «Probabilidad de accidente» se conserva y muestra exactamente
 * «En actualización» mientras el módulo estadístico se desarrolla y valida.
 * No es una retirada definitiva (`RETIRADA`) ni vuelve a publicar las bandas
 * del manual (`PUBLICAR_BANDAS_DEL_MANUAL`), cuya procedencia sigue
 * `UNVERIFIED` (ADR-008). Cambiarla es esta línea, sus pruebas y un despliegue
 * autorizado.
 */
export const ACCIDENT_PROBABILITY_POLICY: AccidentProbabilityPolicy = "EN_ACTUALIZACION";

/**
 * Si la política deja publicar la probabilidad de accidente.
 *
 * Los consumidores deciden con esto y no con `reading`: sin puntaje —menos de
 * {@link MINIMUM_DISTANCE_KM} km, o ninguna familia medida— `reading` es `null`
 * y no dice nada de la política, pero el texto que explica la probabilidad se
 * imprime igual.
 */
export function publishesAccidentProbability(
  policy: AccidentProbabilityPolicy = ACCIDENT_PROBABILITY_POLICY,
): boolean {
  return policy === "PUBLICAR_BANDAS_DEL_MANUAL";
}

export const DRIVESCORE_READINGS: DriveScoreReading[] = [
  { from: 80, risk: "Bajo", clientState: "Excelente", accidentProbability: "6,1 % a 9 % o mejor" },
  { from: 70, risk: "Medio", clientState: "Bueno", accidentProbability: "9,1 % a 12 %" },
  { from: 60, risk: "Alto", clientState: "En observación", accidentProbability: "12,1 % a 16 %" },
  { from: 0, risk: "Crítico", clientState: "Requiere atención", accidentProbability: "16,1 % a 35 %" },
];

/**
 * La lectura de un puntaje. `complete` en false cuando faltó medir algún
 * componente.
 *
 * Con el modelo incompleto se publica el puntaje pero NO la lectura de riesgo
 * ni la probabilidad de accidente. La razón no es de estilo: «Riesgo Bajo,
 * 6,1 % a 9 %» es una afirmación actuarial, y hacerla cuando falta el
 * componente que pesa 35 % es afirmar algo que los datos no sostienen — en un
 * informe que va a una aseguradora.
 *
 * Es el mismo principio que ya rige el resto: un cero que significa «no se
 * pudo medir» no se presenta como «no ocurrió» (ADR-007). Aquí, un puntaje
 * alto que significa «lo poco que medimos salió bien» no se presenta como
 * «conductor de bajo riesgo».
 */
export function readingFor(
  score: number,
  complete = true,
  policy: AccidentProbabilityPolicy = ACCIDENT_PROBABILITY_POLICY,
): DriveScoreReading {
  const banda = DRIVESCORE_READINGS.find((item) => score >= item.from) ?? DRIVESCORE_READINGS[3]!;
  // Retirarla no toca el puntaje ni la lectura de riesgo: solo deja de publicar
  // un porcentaje que hoy no tiene procedencia en el repositorio (ADR-008).
  const reading: DriveScoreReading = publishesAccidentProbability(policy)
    ? banda
    : policy === "EN_ACTUALIZACION"
      ? { ...banda, accidentProbability: null, accidentProbabilityInUpdate: true }
      : { ...banda, accidentProbability: null, accidentProbabilityWithdrawn: true };
  if (complete) return reading;
  return {
    ...reading,
    risk: "No concluyente",
    accidentProbability: null,
  };
}

export type ComponentInput = {
  component: ScoreComponent;
  /** Episodios contados de esa familia en el período. */
  episodes: number;
  /**
   * `false` cuando la cadencia del equipo no permite que ese detector se
   * dispare. No es lo mismo que cero episodios, y por eso no se puntúa.
   */
  measured: boolean;
};

export type ComponentResult = {
  component: ScoreComponent;
  label: string;
  episodes: number;
  ratePer100Km: number | null;
  score: number | null;
  /** Peso efectivo tras renormalizar por los componentes medidos. */
  effectiveWeight: number;
  measured: boolean;
};

export type DriveScoreResult = {
  version: typeof DRIVESCORE_VERSION;
  manual: typeof DRIVESCORE_MANUAL;
  /** `null` cuando no hay distancia, o cuando no se pudo medir ningún componente. */
  driveScore: number | null;
  reading: DriveScoreReading | null;
  distanceKm: number;
  components: ComponentResult[];
  /** Componentes que quedaron fuera por no poderse medir. */
  unmeasured: ScoreComponent[];
  /** `true` si el puntaje se calculó sobre menos de los cuatro componentes. */
  partial: boolean;
  explanation: string;
};

/** Distancia mínima para publicar una tasa. Misma razón que en toda la flota. */
export const MINIMUM_DISTANCE_KM = 20;

const round1 = (value: number) => Math.round(value * 10) / 10;
const round2 = (value: number) => Math.round(value * 100) / 100;
const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

/**
 * Conversión común de episodios consolidados a tasa y puntos.
 *
 * Devuelve `null` cuando no hay exposición válida. La decisión de si un
 * componente es medible y de si la distancia alcanza para publicar sigue en
 * `computeDriveScore`; esta función no convierte ausencia de datos en 100.
 * Devuelve precisión completa: los redondeos de tasa y componente pertenecen
 * a la presentación, y el promedio global solo se redondea al final.
 */
export function scoreFromEpisodeFrequency(
  episodes: number,
  distanceKm: number,
): { ratePer100Km: number; score: number } | null {
  if (!Number.isFinite(episodes) || episodes < 0 || !Number.isFinite(distanceKm) || distanceKm <= 0) {
    return null;
  }
  const ratePer100Km = (100 * episodes) / distanceKm;
  return {
    ratePer100Km,
    score: clamp(100 - ratePer100Km, 0, 100),
  };
}

/**
 * Reparte 100 entre varios pesos de forma que los redondeados **sumen 100**.
 *
 * Redondear cada uno por su cuenta deja 46,7 + 26,7 + 26,7 = 100,1, y unos
 * porcentajes que no suman cien se leen como un error de cálculo en un informe
 * que va a un cliente. Método del resto mayor: el sobrante va a los que más
 * decimal perdieron, con desempate estable por orden para que sea reproducible.
 */
function distributeToHundred(weights: number[]): number[] {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (total <= 0) return weights.map(() => 0);

  const exact = weights.map((weight) => (weight / total) * 100);
  const floored = exact.map((value) => Math.floor(value * 10) / 10);
  const missing = Math.round((100 - floored.reduce((sum, value) => sum + value, 0)) * 10);

  // El resto se compara redondeado a seis decimales. Sin eso, dos restos que
  // son el mismo número (46,66… y 26,66…) difieren en la última cifra binaria y
  // el desempate lo termina decidiendo el ruido de coma flotante en vez de la
  // regla declarada. Sigue siendo determinístico, pero deja de ser explicable.
  const remainderOf = (value: number) =>
    Math.round((value * 10 - Math.floor(value * 10)) * 1e6) / 1e6;
  const order = exact
    .map((value, index) => ({ index, remainder: remainderOf(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);

  const out = [...floored];
  for (let i = 0; i < missing; i++) {
    const target = order[i % order.length]!.index;
    out[target] = round1(out[target]! + 0.1);
  }
  return out;
}

export function computeDriveScore(input: {
  components: ComponentInput[];
  distanceKm: number;
  /** Por defecto, la vigente ({@link ACCIDENT_PROBABILITY_POLICY}). */
  accidentProbabilityPolicy?: AccidentProbabilityPolicy;
}): DriveScoreResult {
  const { distanceKm } = input;
  const enough = distanceKm >= MINIMUM_DISTANCE_KM;

  // Primero se valida cada frecuencia. Esta lista es la única fuente de verdad
  // para ponderación, parcialidad y lectura: un NaN o un conteo negativo no
  // puede quedarse en el denominador después de que su componente fue retirado.
  const evaluated = input.components.map((item) => ({
    item,
    frequency: item.measured ? scoreFromEpisodeFrequency(item.episodes, distanceKm) : null,
  }));
  const measured = evaluated.filter(
    (entry): entry is typeof entry & { frequency: NonNullable<typeof entry.frequency> } =>
      entry.frequency !== null,
  );
  const totalWeight = measured.reduce(
    (sum, entry) => sum + COMPONENT_WEIGHTS[entry.item.component],
    0,
  );

  // Pesos efectivos que suman exactamente 100 entre los componentes medidos.
  const shares = distributeToHundred(
    measured.map((entry) => COMPONENT_WEIGHTS[entry.item.component]),
  );
  const shareOf = new Map(
    measured.map((entry, index) => [entry.item.component, shares[index] ?? 0]),
  );

  const components: ComponentResult[] = evaluated.map(({ item, frequency }) => {
    if (!frequency) {
      return {
        component: item.component,
        label: COMPONENT_LABELS[item.component],
        episodes: item.episodes,
        ratePer100Km: null,
        score: null,
        effectiveWeight: 0,
        measured: false,
      };
    }
    if (!enough) {
      return {
        component: item.component,
        label: COMPONENT_LABELS[item.component],
        episodes: item.episodes,
        ratePer100Km: null,
        score: null,
        effectiveWeight: 0,
        measured: true,
      };
    }
    return {
      component: item.component,
      label: COMPONENT_LABELS[item.component],
      episodes: item.episodes,
      ratePer100Km: frequency.ratePer100Km,
      score: frequency.score,
      // Renormalizado: los componentes que no se midieron no diluyen a los que sí.
      effectiveWeight: shareOf.get(item.component) ?? 0,
      measured: true,
    };
  });

  const unmeasured = components.filter((item) => !item.measured).map((item) => item.component);

  if (!enough) {
    return {
      version: DRIVESCORE_VERSION,
      manual: DRIVESCORE_MANUAL,
      driveScore: null,
      reading: null,
      distanceKm: round2(distanceKm),
      components,
      unmeasured,
      partial: unmeasured.length > 0,
      explanation:
        `Se recorrieron ${round1(distanceKm)} km, por debajo de los ${MINIMUM_DISTANCE_KM} km mínimos. ` +
        "Con tan poca distancia un episodio suelto mueve el puntaje varios puntos, así que no se publica.",
    };
  }

  if (!measured.length) {
    return {
      version: DRIVESCORE_VERSION,
      manual: DRIVESCORE_MANUAL,
      driveScore: null,
      reading: null,
      distanceKm: round2(distanceKm),
      components,
      unmeasured,
      partial: true,
      explanation:
        "Ningún componente se pudo medir con la cadencia de reporte de este equipo. " +
        "No hay puntaje: darle 100 a lo que no se midió sería premiar la falta de datos.",
    };
  }

  const weighted = measured.reduce(
    (sum, entry) =>
      sum +
      entry.frequency.score *
        (COMPONENT_WEIGHTS[entry.item.component] / totalWeight),
    0,
  );
  const driveScore = round1(weighted);
  const reading = readingFor(driveScore, unmeasured.length === 0, input.accidentProbabilityPolicy);

  const explanation =
    `DriveScore ${driveScore} sobre ${round1(distanceKm)} km. ` +
    (unmeasured.length
      ? `Se calculó sobre ${measured.length} de 4 componentes: ${unmeasured
          .map((item) => COMPONENT_LABELS[item].toLowerCase())
          .join(" y ")} no se pudieron medir a esta cadencia, y sus pesos se repartieron entre los demás en vez de contarse como perfectos.`
      : "Los cuatro componentes se pudieron medir.");

  return {
    version: DRIVESCORE_VERSION,
    manual: DRIVESCORE_MANUAL,
    driveScore,
    reading,
    distanceKm: round2(distanceKm),
    components,
    unmeasured,
    partial: unmeasured.length > 0,
    explanation,
  };
}
